import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CatalogForm } from '../../../components/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { ZonaService } from '../../../services/ZonaService';

const ZonaForm = (props) => {
  const routeParams = useParams();
  const id = props.id || props.match?.params?.id || routeParams?.id || null;

  const [tiposOptions, setTiposOptions] = useState([]);
  const [estadosOptions, setEstadosOptions] = useState([]);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [loadingCiudades, setLoadingCiudades] = useState(false);
  const [cachedCiudades, setCachedCiudades] = useState({});

  // Fetch initial form options (Tipos, Estados, initial Ciudades if id exists)
  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      const params = {};
      if (id) params.id_zona = id;
      const data = await ZonaService.obtenerOpcionesForm(params);
      if (isMounted && data) {
        if (data.Tipos) {
          setTiposOptions(data.Tipos.map((t) => ({ value: t.id, label: t.zona_tipo })));
        }
        if (data.Estados) {
          setEstadosOptions(data.Estados.map((e) => ({ value: String(e.id), label: e.estado })));
        }
        if (data.Ciudades) {
          setCachedCiudades((prev) => {
            const next = { ...prev };
            data.Ciudades.forEach((c) => {
              next[String(c.id)] = {
                value: String(c.id),
                label: c.ciudad,
              };
            });
            return next;
          });
          setCiudadesOptions(data.Ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
        }
      }
    };
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Handle dynamic loading of cities when state changes
  const handleEstadoChange = useCallback(async (idEstado) => {
    if (!idEstado) {
      setCiudadesOptions([]);
      return;
    }
    setLoadingCiudades(true);
    const params = { id_estado: idEstado };
    if (id) params.id_zona = id;
    const data = await ZonaService.obtenerOpcionesForm(params);
    setLoadingCiudades(false);
    if (data && data.Ciudades) {
      setCachedCiudades((prev) => {
        const next = { ...prev };
        data.Ciudades.forEach((c) => {
          if (!next[String(c.id)]) {
            next[String(c.id)] = {
              value: String(c.id),
              label: c.ciudad,
            };
          }
        });
        return next;
      });
      setCiudadesOptions(data.Ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
    }
  }, [id]);

  const loadData = useCallback(async (recordId) => {
    const data = await ZonaService.obtenerPorId(recordId);
    if (data) {
      // Register all assigned cities from the backend (including those from other states)
      if (data.ciudades && Array.isArray(data.ciudades)) {
        setCachedCiudades((prev) => {
          const next = { ...prev };
          data.ciudades.forEach((c) => {
            next[String(c.id)] = {
              value: String(c.id),
              label: c.estado ? `${c.ciudad} (${c.estado})` : c.ciudad,
            };
          });
          return next;
        });
      }

      if (data.id_estado) {
        // Refresh cities for the loaded state and zona
        const opts = await ZonaService.obtenerOpcionesForm({ id_estado: data.id_estado, id_zona: recordId });
        if (opts && opts.Ciudades) {
          setCachedCiudades((prev) => {
            const next = { ...prev };
            opts.Ciudades.forEach((c) => {
              if (!next[String(c.id)]) {
                next[String(c.id)] = {
                  value: String(c.id),
                  label: c.ciudad,
                };
              }
            });
            return next;
          });
          setCiudadesOptions(opts.Ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
        }
      }

      if (data.id_ciudades && Array.isArray(data.id_ciudades)) {
        data.id_ciudades = data.id_ciudades.map(String);
      }
    }
    return data;
  }, []);

  const handleSubmit = useCallback(async (values) => {
    return await ZonaService.guardar(values);
  }, []);

  // Merge cities of the currently selected state with any selected cities from other states
  const combinedCiudadesOptions = useMemo(() => {
    const map = new Map();
    // 1. Current state cities
    ciudadesOptions.forEach((c) => {
      map.set(String(c.value), c);
    });
    // 2. Known cities from cache (so multi-state selections keep their labels)
    Object.values(cachedCiudades).forEach((cached) => {
      if (!map.has(String(cached.value))) {
        map.set(String(cached.value), cached);
      }
    });
    return Array.from(map.values());
  }, [ciudadesOptions, cachedCiudades]);

  const fields = useMemo(() => [
    {
      name: 'zona',
      label: 'Clave',
      type: 'text',
      col: 3,
      required: true,
      placeholder: 'Ej. ZN-01',
      validators: [Validators.required, Validators.maxLength(50)],
    },
    {
      name: 'zona_descripcion',
      label: 'Nombre del Lugar',
      type: 'text',
      col: 9,
      required: true,
      placeholder: 'Ej. Zona Metropolitana Norte',
      validators: [Validators.required],
    },
    {
      name: 'id_zona_tipo',
      label: 'Tipo de Zona',
      type: 'select',
      col: 6,
      required: true,
      options: tiposOptions,
      validators: [Validators.required],
    },
    {
      name: 'id_estado',
      label: 'Estado',
      col: 6,
      required: true,
      validators: [Validators.required],
      render: ({ value, error, onChange, onBlur, disabled }) => (
        <div>
          <label htmlFor="id_estado" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            <span className="text-red-500 mr-1">*</span>Estado
          </label>
          <select
            id="id_estado"
            name="id_estado"
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => {
              const newEstado = e.target.value;
              onChange(newEstado);
              handleEstadoChange(newEstado);
            }}
            onBlur={onBlur}
            disabled={disabled}
            className={`w-full px-3.5 py-2.5 min-h-[44px] text-sm text-gray-900 bg-white border rounded-lg transition-colors focus:outline-hidden focus:ring-2 ${
              error
                ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
                : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
            } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          >
            <option value="">Seleccione un estado</option>
            {estadosOptions.map((opt) => (
              <option key={opt.value} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
          {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
      ),
    },
    {
      name: 'id_ciudades',
      label: 'Municipios',
      type: 'select_multiple',
      col: 12,
      required: true,
      options: combinedCiudadesOptions,
      getOptionLabel: (val) => cachedCiudades[String(val)]?.label || String(val),
      helperText: loadingCiudades ? 'Cargando municipios del estado seleccionado...' : undefined,
      validators: [Validators.required],
    },
  ], [tiposOptions, estadosOptions, combinedCiudadesOptions, loadingCiudades, handleEstadoChange, cachedCiudades]);

  return (
    <CatalogForm
      title="Zona"
      subtitle="Administración de zonas geográficas y municipios asignados"
      fields={fields}
      initialValues={{
        zona: '',
        zona_descripcion: '',
        id_zona_tipo: 1,
        id_estado: '',
        id_ciudades: [],
      }}
      id={id}
      loadData={loadData}
      onSubmit={handleSubmit}
      backUrl="/Catalogo/Zonas"
    />
  );
};

export default ZonaForm;
